// /opt/ptg01-v1/front-extranet/app/auth/forgot-password/page.tsx
'use client';

import { useState } from 'react';
import Link from 'next/link';
import { NexviaLogo } from '@nexvia/ui';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const res = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const result = await res.json();
            if (result.success) {
                setSent(true);
            } else {
                setError(result.message || 'Erreur');
            }
        } catch {
            setError('Erreur de communication avec le serveur');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
            <div className="max-w-md w-full space-y-8">
                <div className="flex justify-center">
                    <NexviaLogo />
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
                    {sent ? (
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 rounded-full mb-4">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <h1 className="text-xl font-bold text-slate-900 mb-2">Email envoy&eacute;</h1>
                            <p className="text-slate-500 text-sm mb-6">
                                Si cette adresse est associ&eacute;e &agrave; un compte, vous recevrez un email avec un lien de r&eacute;initialisation. V&eacute;rifiez aussi vos spams.
                            </p>
                            <Link href="/auth/login" className="text-[#000091] hover:text-[#1e40af] font-semibold text-sm flex items-center justify-center gap-1">
                                <ArrowLeft className="w-4 h-4" /> Retour &agrave; la connexion
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-full mb-4">
                                    <Mail className="w-5 h-5 text-[#000091]" />
                                </div>
                                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Mot de passe oubli&eacute;</h1>
                                <p className="text-slate-500 mt-2 text-sm">Saisissez votre email pour recevoir un lien de r&eacute;initialisation.</p>
                            </div>

                            {error && (
                                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">Email</label>
                                    <input
                                        type="email" required autoFocus
                                        className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#000091] focus:border-[#000091] transition-all"
                                        placeholder="votre.email@exemple.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>

                                <button
                                    type="submit" disabled={loading}
                                    className="w-full py-3.5 bg-[#000091] hover:bg-[#1e40af] text-white font-bold rounded-lg shadow-sm transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? "Envoi..." : "Envoyer le lien"}
                                </button>

                                <Link href="/auth/login" className="text-[#000091] hover:text-[#1e40af] font-semibold text-sm flex items-center justify-center gap-1">
                                    <ArrowLeft className="w-4 h-4" /> Retour &agrave; la connexion
                                </Link>
                            </form>
                        </>
                    )}
                </div>

                <p className="text-center text-xs text-slate-400">
                    &copy; {new Date().getFullYear()} Nexvia Portage SAS — Donn&eacute;es h&eacute;berg&eacute;es en France
                </p>
            </div>
        </div>
    );
}
